import { useApolloClient } from "@apollo/client";
import { useCallback, useState } from "react";

import { DocumentsCurrent } from "~/components/Documents/useDocumentsCurrent";
import { DocumentsTableData } from "~/components/Documents/useDocumentsTableData";

import {
  Document_PermissionsFragment,
  Document_PermissionsFragmentDoc,
  useDocumentsUpdatePermissionsMutation,
} from "./DocumentsAPI.generated";

export interface DocumentsPermissions {
  visible: boolean;
  error: string | null;
  loading: boolean;
  document: Document_PermissionsFragment | null;
  canEdit(): boolean;
  edit(documentId: string): void;
  update(documentPermissionId: string, permissible: boolean): Promise<void>;
  close(): void;
  updateSelected(principalId: string, permissible: boolean): Promise<void>;
  updateSelectedDisabled: boolean;
  updateSelectedVisible: boolean;
  setUpdateSelectedVisible: (visible: boolean) => void;
}

/** Handles state and logic required for updating document permissions. */
export const useDocumentsPermissions = (
  treeId: string,
  { tree }: DocumentsCurrent,
  { selection }: DocumentsTableData
): DocumentsPermissions => {
  const client = useApolloClient();
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [updateSelectedVisible, setUpdateSelectedVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [request, { loading }] = useDocumentsUpdatePermissionsMutation();
  const visible = documentId !== null;
  const updateSelectedDisabled = loading || selection.count === 0;

  const document =
    (documentId &&
      client.readFragment<Document_PermissionsFragment>({
        id: `Document:${documentId}`,
        fragment: Document_PermissionsFragmentDoc,
      })) ||
    null;

  const canEdit = useCallback(() => {
    return tree?.canEditPermissions || false;
  }, [tree?.canEditPermissions]);

  const edit = useCallback((id: string) => setDocumentId(id), []);

  const close = useCallback(() => {
    setDocumentId(null);
    setError(null);
    setUpdateSelectedVisible(false);
  }, []);

  /** Toggles a single permission */
  const update = useCallback(
    async (documentPermissionId: string, permissible: boolean) => {
      try {
        await request({
          variables: { treeId, documentPermissionId, permissible },
          refetchQueries: ["OAndM"],
        });
      } catch (error) {
        setError("A server error has occurred");
      }
    },
    [request, treeId]
  );

  /** Toggles a single permission for many documents */
  const updateSelected = useCallback(
    async (principalId: string, permissible: boolean) => {
      try {
        const documentIds = selection.values().map((d) => d.id);
        const documentPermissionId = documentIds.join(",") + ":" + principalId;
        await request({
          variables: { treeId, documentPermissionId, permissible },
        });
      } catch (error) {
        setError("A server error has occurred");
      }
    },
    [request, treeId, selection]
  );

  return {
    visible,
    error,
    loading,
    document,
    canEdit,
    edit,
    update,
    updateSelected,
    updateSelectedDisabled,
    updateSelectedVisible,
    setUpdateSelectedVisible,
    close,
  };
};
